import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getBranches } from '../../services/branchService'
import type { BranchResponse, BranchStatus } from '../../types'
import BranchFormModal from '../../components/BranchForm/BranchFormModal'
import './BranchesPage.css'

const STATUSES: BranchStatus[] = ['PENDING', 'ACTIVE', 'SUSPENDED']

export default function BranchesPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [branches, setBranches] = useState<BranchResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)

  const status = (searchParams.get('status') as BranchStatus) || ''

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBranches(status as BranchStatus || undefined)
      setBranches(data)
    } catch (err) {
      console.error('[ERROR] Failed to load branches', err)
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  function setStatus(s: string) {
    const next = new URLSearchParams(searchParams)
    if (s) next.set('status', s)
    else next.delete('status')
    setSearchParams(next)
  }

  return (
    <div className="branches-page">
      <div className="branches-page__header">
        <h1 className="branches-page__title">{t('admin.branches.title')}</h1>
        <button className="admin-btn admin-btn--primary" onClick={() => setShowCreate(true)}>
          {t('admin.branches.new_branch')}
        </button>
      </div>

      <div className="branches-page__filters">
        <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('admin.branches.all_statuses')}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="admin-table">
        <div className="admin-table__head branches-page__cols">
          <span>{t('admin.branches.col_name')}</span>
          <span>{t('admin.branches.col_address')}</span>
          <span>{t('admin.branches.col_locale')}</span>
          <span>{t('admin.branches.col_status')}</span>
          <span>{t('admin.branches.col_created')}</span>
        </div>

        {loading ? (
          <div className="admin-table__empty"><div className="admin-spinner" /></div>
        ) : branches.length === 0 ? (
          <div className="admin-table__empty">{t('admin.branches.no_branches')}</div>
        ) : (
          branches.map((b) => (
            <div
              key={b.id}
              className={`admin-table__row branches-page__cols${b.archivedAt ? ' admin-table__row--archived' : ''}`}
              onClick={() => navigate(`/admin/branches/${b.id}`)}
            >
              <span className="admin-table__primary">{b.name}</span>
              <span className="admin-table__secondary">{b.address}</span>
              <span className="admin-table__secondary">{b.localeCode?.toUpperCase()}</span>
              <span><BranchStatusBadge status={b.status} /></span>
              <span className="admin-table__secondary">{formatDate(b.createdAt)}</span>
            </div>
          ))
        )}
      </div>

      {showCreate && (
        <BranchFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load() }}
        />
      )}
    </div>
  )
}

export function BranchStatusBadge({ status }: { status: BranchStatus }) {
  const meta: Record<BranchStatus, { color: string }> = {
    PENDING:  { color: '#eab308' },
    ACTIVE:   { color: '#10b981' },
    SUSPENDED:{ color: '#ef4444' },
    ARCHIVED: { color: '#5a5d72' },
  }
  const { color } = meta[status] ?? { color: '#8b8fa8' }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
      background: `${color}1a`, color,
    }}>
      {status}
    </span>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
